import React, { useState, useRef, useEffect, useCallback } from 'react';
import { sendMessage, updateUserTypingStatus, uploadChatFile } from '../../services/chatService'; // Adjust path if needed
import { FaPaperPlane, FaPaperclip, FaSmile, FaSpinner } from 'react-icons/fa'; // Using react-icons
import Picker from 'emoji-picker-react'; // <-- Import the emoji picker
import { toast } from 'react-hot-toast';

// Debounce function (simple implementation) - Keep as is
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
};

const MessageInput = ({ chatId, senderId, receiverId }) => {
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false); // <-- State for picker visibility
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const inputRef = useRef(null); // <-- Ref for the text input
  const pickerRef = useRef(null); // <-- Ref for the picker container
  const emojiButtonRef = useRef(null); // <-- Ref for the emoji button

  // --- Typing Status Logic (Keep as is) ---
  const signalStopTyping = useCallback(() => {
    updateUserTypingStatus(chatId, senderId, false);
  }, [chatId, senderId]);

  const debouncedStopTyping = useCallback(debounce(signalStopTyping, 1500), [signalStopTyping]);

  const handleInputChange = (e) => {
    const newText = e.target.value;
    setMessageText(newText);
    if (!typingTimeoutRef.current) {
      updateUserTypingStatus(chatId, senderId, true);
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      signalStopTyping();
      typingTimeoutRef.current = null;
    }, 1500);
  };

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (chatId && senderId) {
        signalStopTyping();
      }
    };
  }, [chatId, senderId, signalStopTyping]);
  // --- End Typing Status Logic ---


  // --- Emoji Picker Logic ---
  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
  };

  const handleEmojiClick = (emojiData, event) => {
    // emojiData provides the selected emoji (check library docs for exact structure)
    // Usually emojiData.emoji is what you want
    setMessageText(prevText => prevText + emojiData.emoji);
    // Optional: focus the input after adding an emoji
    // inputRef.current?.focus();
    // Keep the picker open if you want to select multiple emojis
    // setShowEmojiPicker(false); // Close picker after selection
  };

  // Effect to close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if picker is open
      // Check if the click is outside the picker container (pickerRef)
      // Check if the click is *not* on the emoji toggle button (emojiButtonRef)
      if (
        showEmojiPicker &&
        pickerRef.current &&
        !pickerRef.current.contains(event.target) &&
        emojiButtonRef.current &&
        !emojiButtonRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    // Add listener if picker is open
    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      // Remove listener if picker is closed
      document.removeEventListener('mousedown', handleClickOutside);
    }

    // Cleanup function to remove listener on unmount or when picker closes
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]); // Re-run effect when showEmojiPicker changes
  // --- End Emoji Picker Logic ---


  // --- File Handling Logic (Keep as is) ---
  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !chatId || !senderId || !receiverId) {
        event.target.value = '';
        return;
    }
    setIsSending(true);
    setUploadProgress(0);
    try {
        const uploadedFile = await uploadChatFile(
            chatId,
            senderId,
            file,
            (progress) => setUploadProgress(progress)
        );
        const messageType = uploadedFile.type.startsWith('image/') ? 'image' : 'file';
        await sendMessage(chatId, senderId, receiverId, {
            type: messageType,
            fileUrl: uploadedFile.url,
            fileName: uploadedFile.name,
            fileType: uploadedFile.type,
        });
        toast.success(`${messageType === 'image' ? 'Image' : 'File'} sent!`);
    } catch (error) {
        console.error("Error uploading or sending file message:", error);
        toast.error(`Failed to send file: ${error?.message || 'Unknown error'}`);
    } finally {
        setIsSending(false);
        setUploadProgress(null);
        event.target.value = '';
    }
  };
  // --- End File Handling Logic ---


  // --- Send Message Logic (Keep as is, but ensure picker closes if open) ---
  const handleSendMessage = async (e) => {
    e.preventDefault();
    const trimmedMessage = messageText.trim();

    if (!trimmedMessage || !chatId || !senderId || !receiverId) {
      return;
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    signalStopTyping();

    // Close emoji picker if it's open when sending
    if (showEmojiPicker) {
        setShowEmojiPicker(false);
    }

    setIsSending(true);
    try {
      await sendMessage(chatId, senderId, receiverId, {
        type: 'text',
        text: trimmedMessage,
      });
      setMessageText('');
    } catch (error) {
      console.error("Failed to send message from input:", error);
      toast.error("Failed to send message.");
    } finally {
      setIsSending(false);
    }
  };
  // --- End Send Message Logic ---
  return (
    <div className="p-2 border-t border-gray-200 bg-gray-50 relative dark:bg-gray-800 dark:border-gray-700">
      {/* Upload Progress Bar */}
      {uploadProgress !== null && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-indigo-200 dark:bg-indigo-800 -mt-2">
          <div
            className="h-1 bg-indigo-600 dark:bg-indigo-400 transition-all duration-300 ease-linear"
            style={{ width: `${uploadProgress}%` }}
          ></div>
        </div>
      )}
  
      {/* Emoji Picker */}
      {showEmojiPicker && (
        <div
          ref={pickerRef}
          style={{
            position: 'absolute',
            bottom: '60px',
            left: '45px',
            zIndex: 10,
          }}
        >
          <Picker
            onEmojiClick={handleEmojiClick}
            pickerStyle={{
              boxShadow: '0 5px 10px rgba(0,0,0,0.1)',
              border: '1px solid #ccc',
              backgroundColor: 'var(--picker-bg)',
            }}
            height={350}
            width={300}
            theme="auto"
          />
        </div>
      )}
  
      {/* Form */}
      <form onSubmit={handleSendMessage} className="flex items-center">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          disabled={isSending}
        />
  
        <button
          type="button"
          onClick={handleFileButtonClick}
          disabled={isSending}
          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white disabled:opacity-50"
          aria-label="Attach file"
        >
          <FaPaperclip />
        </button>
  
        <button
          ref={emojiButtonRef}
          type="button"
          onClick={toggleEmojiPicker}
          disabled={isSending}
          className={`p-2 hover:text-gray-700 disabled:opacity-50 ${
            showEmojiPicker
              ? 'text-indigo-500 dark:text-indigo-400'
              : 'text-gray-500 dark:text-gray-400'
          }`}
          aria-label="Add emoji"
        >
          <FaSmile />
        </button>
  
        <input
          ref={inputRef}
          type="text"
          value={messageText}
          onChange={handleInputChange}
          placeholder="Type a message..."
          className="flex-grow px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-full focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 mx-2 text-sm"
          disabled={isSending}
          aria-label="Message Input"
        />
  
        <button
          type="submit"
          disabled={!messageText.trim() || isSending}
          className={`p-2 rounded-full text-white transition-colors duration-150 ease-in-out ${
            !messageText.trim() || isSending
              ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
              : 'bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600'
          }`}
          aria-label="Send Message"
        >
          {isSending ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
        </button>
      </form>
    </div>
  );
  
};

export default MessageInput;